import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEYS } from '../../utils/storage';
import { useOnboardingState } from './useOnboardingState';
import { usePreviewOnboardingAnswer } from './usePreviewOnboardingAnswer';
import { navigateWithReload } from '../../utils';
import { ROUTES } from '../../utils/routes';
import { OnboardingStorageData } from '../../types/onboarding/survey';

interface ProgramStorageData {
  recommendedStage: string | null;
  onboardingData: OnboardingStorageData | null;
  hasRecommendedStage: boolean;
  hasAnswers: boolean;
}

/**
 * Retrieves and validates onboarding data from localStorage
 */
const getProgramStorageData = (): ProgramStorageData => {
  const onboardingDataStr = localStorage.getItem(STORAGE_KEYS.ONBOARDING_DATA);

  let onboardingData: OnboardingStorageData | null = null;

  if (onboardingDataStr) {
    try {
      const parsed = JSON.parse(onboardingDataStr);
      if (parsed?.answers && parsed?.recommendedStage) {
        onboardingData = parsed;
      }
    } catch (error) {
      // Error parsing onboarding answers
    }
  }

  return {
    recommendedStage: onboardingData?.recommendedStage || null,
    onboardingData,
    hasRecommendedStage: Boolean(onboardingData?.recommendedStage),
    hasAnswers: Boolean(onboardingData?.answers),
  };
};

/**
 * Validates that required program data exists
 */
const validateProgramData = (
  storageData: ProgramStorageData,
): { isValid: boolean; errorMessage: string | null } => {
  if (!storageData.hasRecommendedStage) {
    return {
      isValid: false,
      errorMessage: null,
    };
  }

  if (!storageData.hasAnswers) {
    return {
      isValid: false,
      errorMessage: 'No survey answers found. Please complete the onboarding survey first.',
    };
  }

  if (!storageData.onboardingData?.answers) {
    return {
      isValid: false,
      errorMessage: 'Unable to load your survey answers. Please try starting the survey again.',
    };
  }

  return { isValid: true, errorMessage: null };
};

export const useProgramData = () => {
  const [localError, setLocalError] = useState<string | null>(null);

  const handleError = () => {
    localStorage.removeItem(STORAGE_KEYS.ONBOARDING_DATA);
  };

  const { stageData, handlerGetStageData, isLoadingStage } = useOnboardingState(handleError);
  const {
    handlePreviewAnswer,
    data: previewData,
    loading: isLoadingPreview,
    error: previewError,
  } = usePreviewOnboardingAnswer();

  const fetchProgramData = useCallback(
    (storageData: ProgramStorageData) => {
      if (storageData.recommendedStage) {
        handlerGetStageData(storageData.recommendedStage);
      }

      if (storageData.onboardingData?.answers) {
        handlePreviewAnswer(storageData.onboardingData.answers);
      }
    },
    [handlerGetStageData, handlePreviewAnswer],
  );

  useEffect(() => {
    const loadProgramData = () => {
      try {
        setLocalError(null);

        const storageData = getProgramStorageData();
        const validation = validateProgramData(storageData);

        // Redirect if no recommended stage
        if (!storageData.hasRecommendedStage) {
          navigateWithReload(ROUTES.ONBOARDING);
          return;
        }

        // Set error if validation failed
        if (!validation.isValid && validation.errorMessage) {
          setLocalError(validation.errorMessage);
          return;
        }

        // Fetch data
        fetchProgramData(storageData);
      } catch (error) {
        setLocalError('An unexpected error occurred. Please try again.');
      }
    };

    loadProgramData();
  }, [fetchProgramData]);

  const handleRetry = useCallback(() => {
    setLocalError(null);
    const storageData = getProgramStorageData();
    fetchProgramData(storageData);
  }, [fetchProgramData]);

  const hasError = localError || previewError;
  const errorMessage =
    localError ||
    (previewError ? 'Failed to load program recommendations. Please try again.' : null);

  const isLoading = isLoadingPreview || isLoadingStage;
  const programData = previewData?.previewOnboardingAnswer?.path;
  const fallbackData = stageData?.stages?.[0];

  const hasData = Boolean(programData || (stageData && stageData?.length > 0));

  return {
    isLoading,
    hasError,
    errorMessage,
    hasData,
    programData,
    fallbackData,
    handleRetry,
  };
};
